import React from 'react';
import Layout from '../components/layout/Layout';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
);

const TermsPage = () => {
  return (
    <Layout title="Terms of Service" showBack>
      <div className="bg-white px-4 py-6" data-testid="terms-page">
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-500 text-sm mb-6">Last updated: June 2026</p>

          <Section title="1. Agreement to Terms">
            <p className="text-gray-600 mb-3">
              By accessing or using Minted ("the Platform") at mintedbuysell.com.au you agree to be
              bound by these Terms of Service. If you do not agree, please do not use the Platform.
            </p>
            <p className="text-gray-600">
              We may modify these terms at any time. Continued use after changes constitutes
              acceptance. These terms are governed by the laws of Australia.
            </p>
          </Section>

          <Section title="2. The Marketplace">
            <p className="text-gray-600">
              Minted is a peer-to-peer marketplace. We provide the platform that connects buyers and
              sellers; we are not a party to the sale itself. Sellers — not Minted — are responsible
              for their listings, item condition, accurate descriptions and fulfilment. Nothing in
              these terms limits your rights under the Australian Consumer Law.
            </p>
          </Section>

          <Section title="3. Accounts">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>You must be at least 18 years old, or have parental consent</li>
              <li>Provide accurate registration information and keep it up to date</li>
              <li>Keep your credentials secure; you are responsible for activity on your account</li>
              <li>One account per person; accounts are not transferable</li>
              <li>We may suspend or terminate accounts that breach these terms</li>
            </ul>
          </Section>

          <Section title="4. Buying, Payments & Fees">
            <ul className="list-disc pl-5 text-gray-600 space-y-2 mb-4">
              <li>All online payments are processed securely by <strong>Stripe</strong>. We never see your card details.</li>
              <li>A flat <strong>Card Processing Fee</strong> (currently A$1.00) applies to each online card purchase to cover payment processing costs. It is shown as a line item before you pay. Local pickup arranged privately with cash involves no Minted fee.</li>
              <li>Shipping costs, where applicable, are shown at checkout before payment.</li>
              <li>Prices may be displayed in your local currency for convenience; the charged amount is the listing currency amount shown at checkout.</li>
              <li>Once payment succeeds you will receive an order confirmation with a full receipt.</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">Refunds &amp; cancellations:</h3>
            <p className="text-gray-600">
              If an order is cancelled or a dispute is resolved in the buyer's favour, the buyer is
              refunded in full, including any card processing fee.
            </p>
          </Section>

          <Section title="5. Selling, Funds & Payouts">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>You must have the legal right to sell every item you list, with accurate photos and descriptions</li>
              <li>Sale proceeds (item price plus any shipping collected) are credited to your Minted wallet as a <strong>pending balance</strong></li>
              <li>Funds are held for <strong>7 days</strong> (or until the buyer confirms receipt, whichever is sooner) as buyer protection, then released to your available balance</li>
              <li>Payout withdrawals incur a <strong>A$0.50 processing fee</strong>; the minimum payout is <strong>A$5.00</strong></li>
              <li>Optional listing promotions are available: Bump A$0.99, Urgent badge A$1.99, Boost A$2.99, Featured A$4.99</li>
              <li>Ship promptly (within 3 business days) and use the provided tracking where applicable</li>
            </ul>
          </Section>

          <Section title="6. Disputes & Buyer Protection">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>If an item doesn't arrive or is significantly not as described, open a dispute from your order within the 7-day protection window</li>
              <li>Funds stay held while a dispute is open</li>
              <li>Minted reviews disputes and may refund the buyer or release funds to the seller</li>
              <li>Deliberately abusing the dispute system may result in account termination</li>
            </ul>
          </Section>

          <Section title="7. Prohibited Items & Conduct">
            <p className="text-gray-600 mb-3">You must not list or sell:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-2 mb-4">
              <li>Illegal, stolen or counterfeit goods</li>
              <li>Weapons, drugs, tobacco or vaping products</li>
              <li>Recalled or unsafe products (including non-compliant children's items)</li>
              <li>Items that infringe intellectual property rights</li>
            </ul>
            <p className="text-gray-600 mb-3">You must not:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>Arrange payment outside the Platform to avoid fees or protections</li>
              <li>Harass, defraud or spam other users</li>
              <li>Attempt to access other users' accounts or our systems without authorisation</li>
              <li>Use automated tools to scrape the Platform</li>
            </ul>
          </Section>

          <Section title="8. Shipping & International Sales">
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>Sellers may offer domestic shipping, local pickup, and optionally international shipping</li>
              <li>Shipping quotes are provided through our carrier partners (Shippit / EasyPost) and shown at checkout</li>
              <li>For international orders, the buyer is responsible for any customs duties or import taxes</li>
            </ul>
          </Section>

          <Section title="9. Content & Licence">
            <p className="text-gray-600">
              You retain ownership of the photos and text you upload, and grant Minted a
              non-exclusive licence to display them on the Platform and in Minted marketing (e.g.
              featured listings, product feeds). We may remove content that breaches these terms.
            </p>
          </Section>

          <Section title="10. Liability">
            <p className="text-gray-600 mb-3">
              To the maximum extent permitted by law, Minted is not liable for the conduct of buyers
              or sellers, the quality or legality of listed items, or indirect or consequential loss.
              Our total liability for any claim is limited to the fees you paid to Minted in the 12
              months prior to the claim.
            </p>
            <p className="text-gray-600">
              Nothing in these terms excludes rights that cannot be excluded under the Australian
              Consumer Law.
            </p>
          </Section>

          <Section title="11. Termination">
            <p className="text-gray-600">
              You may close your account at any time via the Delete Account page. We may suspend or
              terminate accounts that breach these terms. On termination, pending legitimate sale
              funds will still be paid out, and records we must keep by law are retained per our
              Privacy Policy.
            </p>
          </Section>

          <Section title="12. Contact">
            <p className="text-gray-600">
              Questions about these terms: contact us via the in-app support chat or at
              admin@mintedbuysell.com.au.
            </p>
          </Section>
        </div>
      </div>
    </Layout>
  );
};

export default TermsPage;
